import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Ticket, Calendar, TrendingUp, Plus, ArrowRight, Users, Zap, Crown, MessageSquare, BarChart3, Wallet, Package, Clock, DollarSign, Download, ScanLine, Link2, AlertTriangle, Megaphone, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/apiUrl';
import CouponCodes from './CouponCodes';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function Dashboard() {
  const { user, isOrganizer, isAdmin, upgradeToOrganizer, loading: authLoading } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  // Check if user can create events (organizers or super_admin only)
  const canCreateEvents = isOrganizer || (user?.role === 'admin' && user?.admin_role === 'super_admin');
  
  // Check if user can view tickets (attendees, organizers, and super_admin only - not finance/support/moderator admins)
  const canViewTickets = user?.role === 'attendee' || isOrganizer || (user?.role === 'admin' && user?.admin_role === 'super_admin');

  // Check if user can view analytics (organizers, finance admin, and super admin only)
  const canViewAnalytics = isOrganizer || (user?.role === 'admin' && ['super_admin', 'finance'].includes(user?.admin_role));

  useEffect(() => {
    const fetchData = async () => {
      // Skip fetching if user is not authorized
      if (!isOrganizer && !isAdmin) return;
      
      try {
        // Only fetch tickets for users who can view them
        if (canViewTickets) {
          const ticketsRes = await axios.get(`${API_URL}/api/tickets/my-tickets`);
          setTickets(ticketsRes.data);
        }

        if (isOrganizer || canCreateEvents) {
          const [eventsRes, subRes] = await Promise.all([
            axios.get(`${API_URL}/api/events/organizer/my-events`),
            axios.get(`${API_URL}/api/subscriptions/my-subscription`)
          ]);
          setMyEvents(eventsRes.data);
          setSubscription(subRes.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isOrganizer, isAdmin, canCreateEvents, canViewTickets]);

  // ROLE CHECK: Only organizers and admins can access the dashboard
  // Regular attendees should be redirected to /my-tickets
  if (!authLoading && user && !isOrganizer && !isAdmin) {
    return <Navigate to="/my-tickets" replace />;
  }

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      await upgradeToOrganizer();
      window.location.reload();
    } catch (error) {
      console.error('Upgrade error:', error);
    } finally {
      setUpgrading(false);
    }
  };

  const upcomingTickets = tickets.filter(t => new Date(t.event_date) >= new Date());
  const pastTickets = tickets.filter(t => new Date(t.event_date) < new Date());

  // Determine if an event is expired: use end_date if available, otherwise date
  const isEventPast = (e) => {
    if (e.is_recurring) return false; // Recurring events are always active
    const eventDate = e.end_date || e.date;
    if (!eventDate) return false; // Events without any date stay in active
    const d = new Date(eventDate);
    if (isNaN(d.getTime())) return false; // Invalid date stays in active
    return d < new Date();
  };

  const activeEvents = myEvents.filter(e => !isEventPast(e));
  const pastEvents = myEvents.filter(e => isEventPast(e));

  const handleExportCSV = async (eventId, eventTitle) => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets/export/${eventId}`, {
        
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${eventTitle.replace(/[^a-zA-Z0-9 _-]/g, '')}_tickets.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Tickets CSV downloaded');
    } catch {
      toast.error('Failed to export tickets');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-1">Welcome, {user?.name?.split(' ')[0]}</h1>
            <p className="text-muted-foreground">
              {isOrganizer ? 'Manage your events and tickets' : 'View your tickets and discover events'}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/support">
              <Button variant="outline" data-testid="support-btn">
                <MessageSquare className="w-4 h-4 mr-2" />
                Support
              </Button>
            </Link>
            {canViewAnalytics && (
              <Link to="/dashboard/analytics">
                <Button variant="outline" data-testid="analytics-btn">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </Button>
              </Link>
            )}
            {isOrganizer && (
              <Link to="/dashboard/earnings">
                <Button variant="outline" data-testid="earnings-btn">
                  <Wallet className="w-4 h-4 mr-2" />
                  Earnings
                </Button>
              </Link>
            )}
            {isOrganizer && (
              <Link to="/dashboard/scanner">
                <Button variant="outline" data-testid="scanner-btn">
                  <ScanLine className="w-4 h-4 mr-2" />
                  Scan Tickets
                </Button>
              </Link>
            )}
            {isOrganizer && (
              <Link to="/dashboard/referrals">
                <Button variant="outline" data-testid="referrals-btn">
                  <Link2 className="w-4 h-4 mr-2" />
                  Referrals
                </Button>
              </Link>
            )}
            {isOrganizer && (
              <Link to="/dashboard/broadcasts">
                <Button variant="outline" data-testid="broadcasts-btn">
                  <Megaphone className="w-4 h-4 mr-2" />
                  Message Attendees
                </Button>
              </Link>
            )}
            {canCreateEvents && (
              <Link to="/dashboard/events/create">
                <Button className="btn-primary" data-testid="create-event-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {canViewTickets && (
            <>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Ticket className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{tickets.length}</p>
                      <p className="text-xs text-muted-foreground">Total Tickets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-secondary/10">
                      <Calendar className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{upcomingTickets.length}</p>
                      <p className="text-xs text-muted-foreground">Upcoming</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          {isOrganizer && (
            <>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10">
                      <TrendingUp className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{myEvents.length}</p>
                      <p className="text-xs text-muted-foreground">My Events</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/10">
                      <Calendar className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{activeEvents.length}</p>
                      <p className="text-xs text-muted-foreground">Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                      <Clock className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{pastEvents.length}</p>
                      <p className="text-xs text-muted-foreground">Past</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Subscription Card for Organizers */}
        {isOrganizer && subscription && (
          <Card className="mb-8 overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div className="flex-1 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600">
                    {subscription.subscription?.tier === 'enterprise' ? (
                      <Crown className="w-5 h-5 text-white" />
                    ) : (
                      <Zap className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold">{subscription.plan_details?.name || 'Starter'} Plan</h3>
                    <p className="text-sm text-muted-foreground">{subscription.plan_details?.tagline}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Events this month</span>
                      <span className="font-medium">
                        {subscription.usage?.events_used} / {subscription.usage?.events_limit}
                      </span>
                    </div>
                    {subscription.usage?.events_limit !== 'Unlimited' && (
                      <Progress 
                        value={(subscription.usage?.events_used / subscription.usage?.events_limit) * 100} 
                        className="h-2"
                      />
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    Ticketing fee: {subscription.plan_details?.ticketing_fee_percent}% on paid tickets
                  </p>
                </div>
              </div>
              
              <div className="lg:w-64 p-6 bg-muted/30 flex flex-col justify-center items-center text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  {subscription.subscription?.tier === 'enterprise' 
                    ? 'You have the best plan!'
                    : 'Upgrade for more events & lower fees'
                  }
                </p>
                <Link to="/dashboard/subscription">
                  <Button 
                    variant={subscription.subscription?.tier === 'enterprise' ? 'outline' : 'default'}
                    className={subscription.subscription?.tier !== 'enterprise' ? 'btn-primary' : ''}
                    data-testid="manage-subscription-btn"
                  >
                    {subscription.subscription?.tier === 'enterprise' ? 'View Plans' : 'Upgrade Plan'}
                  </Button>
                </Link>
              </div>
            </div>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue={isOrganizer ? "events" : (canViewTickets ? "tickets" : "events")} className="space-y-6">
          <TabsList>
            {isOrganizer && <TabsTrigger value="events" data-testid="tab-events">Active Events</TabsTrigger>}
            {isOrganizer && <TabsTrigger value="past-events" data-testid="tab-past-events">Past Events</TabsTrigger>}
            {isOrganizer && <TabsTrigger value="coupons" data-testid="tab-coupons" className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" />Coupon Codes</TabsTrigger>}
            {canViewTickets && <TabsTrigger value="tickets" data-testid="tab-tickets">My Tickets</TabsTrigger>}
          </TabsList>

          {/* Active Events Tab */}
          {isOrganizer && (
            <TabsContent value="events" className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 skeleton rounded-xl" />
                  ))}
                </div>
              ) : activeEvents.length > 0 ? (
                <div className="space-y-4">
                  {activeEvents.map((event) => {
                    const ticketsSold = event.confirmed_tickets_sold ?? 0;
                    const isFreeEvent = event.event_type === 'free';
                    const hasFreeLimit = isFreeEvent && event.free_ticket_limit;
                    const hasUnlimitedTickets = event.ticket_types?.some(t => t.quantity === null || t.quantity === undefined);
                    const totalCapacity = hasFreeLimit
                      ? event.free_ticket_limit
                      : hasUnlimitedTickets ? null : event.ticket_types.reduce((sum, t) => sum + (t.quantity || 0), 0);
                    const isUnlimited = (isFreeEvent && !event.free_ticket_limit) || hasUnlimitedTickets || totalCapacity === null;
                    const ticketsLeft = isUnlimited ? null : Math.max(0, totalCapacity - ticketsSold);
                    return (
                    <Card key={event.event_id} className="overflow-hidden">
                      <div className="flex flex-col sm:flex-row">
                        <div className="w-full sm:w-32 h-24 sm:h-auto flex-shrink-0">
                          <img
                            src={getImageUrl(event.image_url)}
                            alt={event.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardContent className="flex-1 p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{event.title}</h3>
                                {event.approval_status === 'rejected' ? (
                                  <Badge variant="destructive" data-testid={`badge-rejected-${event.event_id}`}>Rejected</Badge>
                                ) : event.approval_status === 'pending' ? (
                                  <Badge variant="outline" className="border-amber-500 text-amber-500" data-testid={`badge-pending-${event.event_id}`}>Pending Review</Badge>
                                ) : (
                                  <Badge variant={event.is_published ? 'default' : 'secondary'}>
                                    {event.is_published ? 'Published' : 'Draft'}
                                  </Badge>
                                )}
                              </div>
                              {event.approval_status === 'rejected' && event.rejection_reason && (
                                <div className="flex items-start gap-1.5 mt-1 mb-1 p-2 rounded-md bg-destructive/10 border border-destructive/20">
                                  <AlertTriangle className="w-3.5 h-3.5 text-destructive mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-destructive" data-testid={`rejection-reason-${event.event_id}`}>{event.rejection_reason}</p>
                                </div>
                              )}
                              <p className="text-sm text-muted-foreground">
                                {format(new Date(event.date), 'EEE, MMM d • h:mm a')} • {event.venue}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <div className="flex items-center gap-1.5">
                                  <Users className="w-4 h-4 text-cyan-500" />
                                  <span className="text-sm font-medium text-cyan-500" data-testid={`tickets-sold-${event.event_id}`}>
                                    {ticketsSold} {isFreeEvent ? 'Claimed' : 'Sold'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Package className="w-4 h-4 text-amber-500" />
                                  <span className="text-sm font-medium text-amber-500" data-testid={`tickets-left-${event.event_id}`}>
                                    {isUnlimited ? 'Unlimited' : `${ticketsLeft} Left`}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Link to={`/dashboard/scanner?event=${event.event_id}`}>
                                <Button variant="outline" size="sm" data-testid={`scan-event-${event.event_id}`}>
                                  <ScanLine className="w-4 h-4 mr-1" />
                                  Scan
                                </Button>
                              </Link>
                              <Link to={`/dashboard/analytics?event=${event.event_id}`}>
                                <Button variant="outline" size="sm" data-testid={`analytics-event-${event.event_id}`}>
                                  <BarChart3 className="w-4 h-4 mr-1" />
                                  Analytics
                                </Button>
                              </Link>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExportCSV(event.event_id, event.title)}
                                data-testid={`export-csv-${event.event_id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Export
                              </Button>
                              <Link to={`/dashboard/events/${event.event_id}/edit`}>
                                <Button variant={event.approval_status === 'rejected' ? 'default' : 'outline'} size="sm" data-testid={`edit-event-${event.event_id}`}>
                                  {event.approval_status === 'rejected' ? 'Edit & Resubmit' : 'Edit'}
                                </Button>
                              </Link>
                              <Link to={`/events/${event.event_id}`}>
                                <Button variant="ghost" size="sm" data-testid={`view-event-${event.event_id}`}>
                                  View
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  )})}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Calendar className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No active events</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Create your first event and start selling tickets
                    </p>
                    <Link to="/dashboard/events/create">
                      <Button className="btn-primary" data-testid="create-first-event-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Event
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Past Events Tab */}
          {isOrganizer && (
            <TabsContent value="past-events" className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 skeleton rounded-xl" />
                  ))}
                </div>
              ) : pastEvents.length > 0 ? (
                <div className="space-y-4">
                  {pastEvents.map((event) => {
                    const ticketsSold = event.confirmed_tickets_sold ?? 0;
                    const isFreeEvent = event.event_type === 'free';
                    const hasFreeLimit = isFreeEvent && event.free_ticket_limit;
                    const hasUnlimitedTickets = event.ticket_types?.some(t => t.quantity === null || t.quantity === undefined);
                    const totalCapacity = hasFreeLimit
                      ? event.free_ticket_limit
                      : hasUnlimitedTickets ? null : event.ticket_types.reduce((sum, t) => sum + (t.quantity || 0), 0);
                    const isUnlimited = (isFreeEvent && !event.free_ticket_limit) || hasUnlimitedTickets || totalCapacity === null;
                    const occupancy = (!isUnlimited && totalCapacity > 0) ? Math.round((ticketsSold / totalCapacity) * 100) : 0;
                    return (
                    <Card key={event.event_id} className="overflow-hidden border-border/60">
                      <div className="flex flex-col sm:flex-row">
                        <div className="w-full sm:w-32 h-24 sm:h-auto flex-shrink-0 relative">
                          <img
                            src={getImageUrl(event.image_url)}
                            alt={event.title}
                            className="w-full h-full object-cover opacity-80"
                          />
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <Badge variant="secondary" className="bg-black/60 text-white border-0 text-xs">
                              Ended
                            </Badge>
                          </div>
                        </div>
                        <CardContent className="flex-1 p-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{event.title}</h3>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {event.date ? format(new Date(event.date), 'EEE, MMM d, yyyy • h:mm a') : 'No date'} • {event.venue}
                              </p>
                              <div className="flex items-center gap-4 mt-2">
                                <div className="flex items-center gap-1.5">
                                  <Users className="w-4 h-4 text-cyan-500" />
                                  <span className="text-sm font-medium text-cyan-500">
                                    {ticketsSold} {isFreeEvent ? 'Claimed' : 'Sold'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <TrendingUp className="w-4 h-4 text-green-500" />
                                  <span className="text-sm font-medium text-green-500">
                                    {isUnlimited ? 'Unlimited' : `${occupancy}% Occupancy`}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Link to={`/dashboard/analytics?event=${event.event_id}`}>
                                <Button variant="default" size="sm" className="btn-primary" data-testid={`analytics-past-event-${event.event_id}`}>
                                  <BarChart3 className="w-4 h-4 mr-1" />
                                  View Analytics
                                </Button>
                              </Link>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExportCSV(event.event_id, event.title)}
                                data-testid={`export-csv-past-${event.event_id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Export
                              </Button>
                              <Link to={`/events/${event.event_id}`}>
                                <Button variant="ghost" size="sm" data-testid={`view-past-event-${event.event_id}`}>
                                  View
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  )})}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Clock className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No past events</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Your completed events will appear here with full analytics
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}

          {/* Coupon Codes Tab */}
          {isOrganizer && (
            <TabsContent value="coupons" className="space-y-4">
              <CouponCodes />
            </TabsContent>
          )}


          {/* My Tickets Tab */}
          {canViewTickets && (
            <TabsContent value="tickets" className="space-y-4">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 skeleton rounded-xl" />
                  ))}
                </div>
              ) : tickets.length > 0 ? (
                <>
                  {upcomingTickets.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-muted-foreground">Upcoming Events</h3>
                      {upcomingTickets.map((ticket) => (
                        <Link 
                          key={ticket.ticket_id} 
                          to={`/dashboard/tickets/${ticket.ticket_id}`}
                          data-testid={`ticket-${ticket.ticket_id}`}
                        >
                          <Card className="hover:border-primary/50 transition-colors">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <Ticket className="w-8 h-8 text-primary" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold truncate">{ticket.event_title}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {format(new Date(ticket.event_date), 'EEE, MMM d • h:mm a')}
                                  </p>
                                  <p className="text-sm text-muted-foreground">{ticket.ticket_type_name}</p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-muted-foreground" />
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  )}

                  {pastTickets.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium text-muted-foreground">Past Events</h3>
                      {pastTickets.map((ticket) => (
                        <Link 
                          key={ticket.ticket_id} 
                          to={`/dashboard/tickets/${ticket.ticket_id}`}
                          data-testid={`ticket-${ticket.ticket_id}`}
                        >
                          <Card className="opacity-60 hover:opacity-80 transition-opacity">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                  <Ticket className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold truncate">{ticket.event_title}</h4>
                                  <p className="text-sm text-muted-foreground">
                                    {format(new Date(ticket.event_date), 'EEE, MMM d • h:mm a')}
                                  </p>
                                </div>
                                <ArrowRight className="w-5 h-5 text-muted-foreground" />
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Ticket className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No tickets yet</h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Discover amazing events and get your first ticket
                    </p>
                    <Link to="/events">
                      <Button className="btn-primary" data-testid="browse-events-btn">
                        Browse Events
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>

        {/* Upgrade to Organizer CTA - only for regular attendees, not admins */}
        {!isOrganizer && user?.role === 'attendee' && (
          <Card className="mt-8 bg-primary/5 border-primary/20">
            <CardContent className="flex flex-col sm:flex-row items-center justify-between gap-4 p-6">
              <div>
                <h3 className="text-lg font-semibold mb-1">Want to host events?</h3>
                <p className="text-muted-foreground">
                  Upgrade to an organizer account to create and manage your own events
                </p>
              </div>
              <Button 
                className="btn-primary flex-shrink-0" 
                onClick={handleUpgrade}
                disabled={upgrading}
                data-testid="upgrade-btn"
              >
                {upgrading ? 'Upgrading...' : 'Become an Organizer'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
